import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import type { InterfaceNamespaceEntity } from '@unifyapps/defs/types/namespace';
import { useMemo } from 'react';
import { adaptDataWithTranslations } from '../components/page/InterfacePageProviders/utils/adaptDataWithTranslations';
import InterfaceHelper from '../stores/InterfaceStore/helper/InterfaceHelper';

interface UseTranslatedInterfaceRecordProps {
  interfaceRecord: InterfaceEntity;
  commonNamespace?: InterfaceNamespaceEntity;
  shouldTranslate?: boolean;
}

function useTranslatedInterfaceRecord({
  interfaceRecord,
  commonNamespace,
  shouldTranslate = true,
}: UseTranslatedInterfaceRecordProps) {
  const defaultLocale = InterfaceHelper.getDefaultLocale(interfaceRecord);
  const locale = commonNamespace?.properties.locale;
  const translations = commonNamespace?.properties.translations;

  const translatedInterfaceRecord = useMemo(() => {
    // if no locale is provided or locale matches default locale, return original
    if (!locale || locale === defaultLocale || !shouldTranslate) {
      return interfaceRecord;
    }

    // if no translations provided, return original
    if (!translations) {
      return interfaceRecord;
    }

    const properties = adaptDataWithTranslations({
      data: interfaceRecord.properties,
      translations,
    });

    return {
      ...interfaceRecord,
      properties,
    } as InterfaceEntity;
  }, [interfaceRecord, shouldTranslate, translations, locale, defaultLocale]);

  return { interfaceRecord: translatedInterfaceRecord };
}

export { useTranslatedInterfaceRecord };
